/**
 * Geography > US Reference (WPS-222) - the Census-derived fallbacks the
 * resolver walks when a postal code is unknown: counties, MSAs, FIPS county
 * bridges and the per-state default zone. Counties / MSAs / FIPS are read-only
 * reference tables (searchable, imported by the seed); state defaults are the
 * one tenant-authored table here and get full CRUD.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Landmark, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  counties,
  fipsCountyZones,
  msas,
  stateDefaults,
  useGeoMeta,
  type CountyRow,
  type FipsCountyZoneRow,
  type MsaRow,
  type StateDefaultRow,
} from '@/admin/hooks/use-geo-reference'

import { CodeCell, GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneOptions, zoneLabel } from './geography-utils'
import { saveWithFieldErrors, useListState } from './section-crud'

export function UsReferenceSection() {
  const { t } = useTranslation()
  return (
    <div data-testid="geography-reference">
      <div className="mb-5">
        <h2 className="clara-heading">{t('geography.reference.title', 'US Reference')}</h2>
        <p className="mt-1 max-w-[720px] text-[13px] leading-relaxed text-[var(--ink-3)]">
          {t(
            'geography.reference.description',
            'Census-derived fallbacks used when a postal code is not in the crosswalk: county, FIPS county bridge, metropolitan statistical area, then the state default zone.',
          )}
        </p>
      </div>
      <Tabs defaultValue="counties">
        <TabsList variant="line" className="mb-6">
          <TabsTrigger value="counties">{t('geography.reference.tabs.counties', 'Counties')}</TabsTrigger>
          <TabsTrigger value="msas">{t('geography.reference.tabs.msas', 'MSAs')}</TabsTrigger>
          <TabsTrigger value="fips">{t('geography.reference.tabs.fips', 'FIPS bridges')}</TabsTrigger>
          <TabsTrigger value="state-defaults">
            {t('geography.reference.tabs.stateDefaults', 'State defaults')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="counties">
          <CountiesTable />
        </TabsContent>
        <TabsContent value="msas">
          <MsasTable />
        </TabsContent>
        <TabsContent value="fips">
          <FipsTable />
        </TabsContent>
        <TabsContent value="state-defaults">
          <StateDefaultsTable />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function ReadOnlyNote() {
  const { t } = useTranslation()
  return (
    <p className="text-[12px] text-[var(--ink-3)]">
      {t(
        'geography.reference.readOnlyNote',
        'Reference data - maintained by the platform seed. Search to check a value; changes land through the seed.',
      )}
    </p>
  )
}

function CountiesTable() {
  const { t } = useTranslation()
  const list = useListState()
  const { data: meta } = useGeoMeta()
  const zoneOptions = useZoneOptions()
  const { data, isLoading, isFetching, isError, refetch } = counties.useList(list.params)
  const countLine = useCountLine(
    data?.count,
    t('geography.reference.counties.countOne', 'county'),
    t('geography.reference.counties.countMany', 'counties'),
  )
  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'state', label: t('geography.reference.column.state', 'State'), kind: 'field', options: meta?.states ?? [] },
      { key: 'zone', label: t('geography.reference.column.zone', 'Zone'), kind: 'field', options: zoneOptions },
    ],
    [meta?.states, t, zoneOptions],
  )
  const columns = useMemo<DataTableColumn<CountyRow>[]>(
    () => [
      { id: 'name', header: t('geography.reference.column.county', 'County'), sortable: true, sortField: 'name', cell: (r) => <TextCell value={r.name} /> },
      { id: 'state', header: t('geography.reference.column.state', 'State'), sortable: true, sortField: 'state', cell: (r) => <TextCell muted value={r.state} /> },
      {
        id: 'fips',
        header: t('geography.reference.column.fips', 'FIPS'),
        // State and county codes shown side by side (the seed stores them as
        // separate fields); a bare concatenation reads as one number.
        cell: (r) => (
          <CodeCell
            value={[r.fips_state_code, r.fips_county_code].filter(Boolean).join(' / ')}
          />
        ),
      },
      { id: 'zone', header: t('geography.reference.column.zone', 'Zone'), cell: (r) => <TextCell muted value={r.zone_name ? zoneLabel(r.zone_name, r.zone_code) : '-'} /> },
      { id: 'msa_count', header: t('geography.reference.column.msas', 'MSAs'), cell: (r) => <TextCell muted value={r.msa_count} /> },
    ],
    [t],
  )
  return (
    <GeographySectionShell
      title={t('geography.reference.tabs.counties', 'Counties')}
      countLine={countLine}
      search={list.search}
      onSearchChange={list.setSearch}
      searchPlaceholder={t('geography.reference.counties.searchPlaceholder', 'Search county or FIPS code')}
      facets={facets}
      filterValues={list.filterValues}
      onFilterValuesChange={list.setFilterValues}
      actions={<ReadOnlyNote />}
    >
      <DataTable<CountyRow>
        tableId="geography-counties"
        columns={columns}
        data={data?.results ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={() => refetch()}
        page={list.page}
        pageSize={list.pageSize}
        totalCount={data?.count ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        rowKey={(r) => r.uuid}
        emptyStateNode={
          <EmptyState icon={Landmark} title={t('geography.reference.counties.emptyTitle', 'No counties')} />
        }
      />
    </GeographySectionShell>
  )
}

function MsasTable() {
  const { t } = useTranslation()
  const list = useListState()
  const { data, isLoading, isFetching, isError, refetch } = msas.useList(list.params)
  const countLine = useCountLine(
    data?.count,
    t('geography.reference.msas.countOne', 'MSA'),
    t('geography.reference.msas.countMany', 'MSAs'),
  )
  const columns = useMemo<DataTableColumn<MsaRow>[]>(
    () => [
      { id: 'code', header: t('geography.common.code', 'Code'), sortable: true, sortField: 'code', cell: (r) => <CodeCell value={r.code} /> },
      { id: 'title', header: t('geography.reference.column.title', 'Title'), sortable: true, sortField: 'title', cell: (r) => <TextCell value={r.title} /> },
      { id: 'statistical_area', header: t('geography.reference.column.area', 'Statistical area'), cell: (r) => <TextCell muted value={r.statistical_area || '-'} /> },
      { id: 'county_count', header: t('geography.reference.column.counties', 'Counties'), cell: (r) => <TextCell muted value={r.county_count} /> },
    ],
    [t],
  )
  return (
    <GeographySectionShell
      title={t('geography.reference.tabs.msas', 'MSAs')}
      countLine={countLine}
      search={list.search}
      onSearchChange={list.setSearch}
      searchPlaceholder={t('geography.reference.msas.searchPlaceholder', 'Search MSA title or code')}
      actions={<ReadOnlyNote />}
    >
      <DataTable<MsaRow>
        tableId="geography-msas"
        columns={columns}
        data={data?.results ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={() => refetch()}
        page={list.page}
        pageSize={list.pageSize}
        totalCount={data?.count ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        rowKey={(r) => r.uuid}
        emptyStateNode={<EmptyState icon={Landmark} title={t('geography.reference.msas.emptyTitle', 'No MSAs')} />}
      />
    </GeographySectionShell>
  )
}

function FipsTable() {
  const { t } = useTranslation()
  const list = useListState()
  const { data: meta } = useGeoMeta()
  const { data, isLoading, isFetching, isError, refetch } = fipsCountyZones.useList(list.params)
  const countLine = useCountLine(
    data?.count,
    t('geography.reference.fips.countOne', 'bridge'),
    t('geography.reference.fips.countMany', 'bridges'),
  )
  const facets = useMemo<ProductFilterFacet[]>(
    () => [{ key: 'state', label: t('geography.reference.column.state', 'State'), kind: 'field', options: meta?.states ?? [] }],
    [meta?.states, t],
  )
  const columns = useMemo<DataTableColumn<FipsCountyZoneRow>[]>(
    () => [
      { id: 'county_name', header: t('geography.reference.column.fipsCounty', 'FIPS county name'), sortable: true, sortField: 'county_name', cell: (r) => <TextCell value={r.county_name} /> },
      { id: 'state', header: t('geography.reference.column.state', 'State'), cell: (r) => <TextCell muted value={r.state} /> },
      { id: 'zone', header: t('geography.reference.column.zone', 'Zone'), cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} /> },
    ],
    [t],
  )
  return (
    <GeographySectionShell
      title={t('geography.reference.tabs.fips', 'FIPS bridges')}
      description={t(
        'geography.reference.fips.description',
        'Bridges the Census county name used in postal data to the zone the tenant knows it by.',
      )}
      countLine={countLine}
      search={list.search}
      onSearchChange={list.setSearch}
      searchPlaceholder={t('geography.reference.fips.searchPlaceholder', 'Search county name')}
      facets={facets}
      filterValues={list.filterValues}
      onFilterValuesChange={list.setFilterValues}
      actions={<ReadOnlyNote />}
    >
      <DataTable<FipsCountyZoneRow>
        tableId="geography-fips"
        columns={columns}
        data={data?.results ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={() => refetch()}
        page={list.page}
        pageSize={list.pageSize}
        totalCount={data?.count ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        rowKey={(r) => r.uuid}
        emptyStateNode={<EmptyState icon={Landmark} title={t('geography.reference.fips.emptyTitle', 'No FIPS bridges')} />}
      />
    </GeographySectionShell>
  )
}

function StateDefaultsTable() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('geo-zones.create')
  const canEdit = can('geo-zones.edit')
  const canDelete = can('geo-zones.delete')
  const list = useListState()
  const { data, isLoading, isFetching, isError, refetch } = stateDefaults.useList(list.params)
  const create = stateDefaults.useCreate()
  const update = stateDefaults.useUpdate()
  const remove = stateDefaults.useDelete()
  const zoneOptions = useZoneOptions()
  const [editing, setEditing] = useState<StateDefaultRow | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<StateDefaultRow | null>(null)
  const countLine = useCountLine(
    data?.count,
    t('geography.reference.stateDefaults.countOne', 'state default'),
    t('geography.reference.stateDefaults.countMany', 'state defaults'),
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'state',
        display_name: t('geography.reference.column.state', 'State'),
        field_type: 'TEXT',
        required: true,
        description: t('geography.reference.stateDefaults.stateHint', 'Two-letter state or province code, e.g. OH.'),
      },
      {
        code_name: 'zone_uuid',
        display_name: t('geography.reference.stateDefaults.field.zone', 'Default zone'),
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
        configuration_data: { searchable: true },
      },
    ],
    [t, zoneOptions],
  )
  const values = useMemo<Record<string, unknown>>(
    () => (editing ? { state: editing.state, zone_uuid: editing.zone_uuid } : {}),
    [editing],
  )
  const columns = useMemo<DataTableColumn<StateDefaultRow>[]>(
    () => [
      { id: 'state', header: t('geography.reference.column.state', 'State'), sortable: true, sortField: 'state', cell: (r) => <CodeCell value={r.state} /> },
      { id: 'zone', header: t('geography.reference.stateDefaults.field.zone', 'Default zone'), cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} /> },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button variant="ghost" size="xs" onClick={() => { setEditing(r); setFormOpen(true) }}>
                  {t('geography.common.edit', 'Edit')}
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                  {t('geography.common.delete', 'Delete')}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canDelete, canEdit, t],
  )

  return (
    <>
      <GeographySectionShell
        title={t('geography.reference.tabs.stateDefaults', 'State defaults')}
        description={t(
          'geography.reference.stateDefaults.description',
          'The zone an address falls back to when nothing more specific matches. One per state.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.reference.stateDefaults.searchPlaceholder', 'Search state or zone')}
        actions={
          canCreate ? (
            <Button size="sm" data-testid="geography-state-default-add-btn" onClick={() => { setEditing(null); setFormOpen(true) }}>
              <Plus className="h-3.5 w-3.5" />
              {t('geography.reference.stateDefaults.add', 'Add state default')}
            </Button>
          ) : null
        }
      >
        <DataTable<StateDefaultRow>
          tableId="geography-state-defaults"
          columns={columns}
          data={data?.results ?? []}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState icon={Landmark} title={t('geography.reference.stateDefaults.emptyTitle', 'No state defaults')} />
          }
        />
      </GeographySectionShell>

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => { setFormOpen(open); if (!open) setEditing(null) }}
        title={editing ? t('geography.reference.stateDefaults.editTitle', 'Edit state default') : t('geography.reference.stateDefaults.addTitle', 'Add state default')}
        fields={fields}
        values={values}
        isPending={create.isPending || update.isPending}
        onSave={(v, _reason, helpers) =>
          saveWithFieldErrors(
            async () => {
              const payload = { state: String(v.state ?? '').trim(), zone_uuid: String(v.zone_uuid) }
              if (editing) await update.mutateAsync({ uuid: editing.uuid, data: payload })
              else await create.mutateAsync(payload)
            },
            helpers,
            editing
              ? t('geography.reference.stateDefaults.updated', 'State default updated.')
              : t('geography.reference.stateDefaults.created', 'State default added.'),
            t('geography.reference.stateDefaults.saveFailed', 'Could not save the state default.'),
          )
        }
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => { if (!open) setDeleting(null) }}
        title={t('geography.reference.stateDefaults.deleteTitle', 'Delete state default?')}
        description={deleting ? t('geography.reference.stateDefaults.deleteDescription', 'Addresses in {{state}} with no closer match will stop resolving.', { state: deleting.state }) : undefined}
        isPending={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await remove.mutateAsync(deleting.uuid)
            toast.success(t('geography.reference.stateDefaults.deleted', 'State default deleted.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(err, t('geography.reference.stateDefaults.deleteFailed', 'Could not delete the state default.'))
          }
        }}
      />
    </>
  )
}
